import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, downloadJobResult } from '../../api.js'
import { Card, Field, Alert, VerdictBadge, JobProgress, useJobElapsed, usePolling } from '../../lib.jsx'

const CONTENT_TYPES = ['text', 'rich_text', 'title', 'subtitle', 'quote', 'bullet_list', 'numbered_list',
  'image', 'generated_image', 'link', 'video', 'flowchart', 'table', 'chart']
const EMPTY_BEHAVIORS = ['hide_partial', 'show_empty', 'placeholder']
const EMPTY_PL = { hide_partial: 'ukryj partial', show_empty: 'pokaż pusty', placeholder: 'wypełnij placeholderem' }

function SlotEditor({ slot, onSaved, onActivate }) {
  const [label, setLabel] = useState(slot.label)
  const [contentType, setContentType] = useState(slot.content_type)
  const [required, setRequired] = useState(slot.required)
  const [minLen, setMinLen] = useState(slot.recommended_length_min ?? '')
  const [maxLen, setMaxLen] = useState(slot.recommended_length_max ?? '')
  const [behavior, setBehavior] = useState(slot.behavior_if_empty)
  const [contentHint, setContentHint] = useState(slot.content_hint || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const dirty = label !== slot.label || contentType !== slot.content_type || required !== slot.required ||
    String(minLen) !== String(slot.recommended_length_min ?? '') || String(maxLen) !== String(slot.recommended_length_max ?? '') ||
    behavior !== slot.behavior_if_empty || contentHint !== (slot.content_hint || '')

  const save = async () => {
    setBusy(true); setErr('')
    try {
      const updated = await api.patchSlot(slot.id, {
        label, content_type: contentType, required,
        recommended_length_min: minLen === '' ? null : Number(minLen),
        recommended_length_max: maxLen === '' ? null : Number(maxLen),
        behavior_if_empty: behavior, content_hint: contentHint,
      })
      onSaved(updated)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <div className="unit-row" onFocus={() => onActivate && onActivate(slot.data_content)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <div className="mono small">{slot.data_content}</div>
        {slot.required && <span className="pill pill-error">wymagany</span>}
      </div>
      <div className="row">
        <Field label="Etykieta"><input value={label} onChange={e => setLabel(e.target.value)} /></Field>
        <Field label="Typ treści">
          <select value={contentType} onChange={e => setContentType(e.target.value)}>
            {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label="Min. długość (znaki)"><input type="number" value={minLen} onChange={e => setMinLen(e.target.value)} /></Field>
        <Field label="Maks. długość (znaki)"><input type="number" value={maxLen} onChange={e => setMaxLen(e.target.value)} /></Field>
        <Field label="Gdy pusty">
          <select value={behavior} onChange={e => setBehavior(e.target.value)}>
            {EMPTY_BEHAVIORS.map(b => <option key={b} value={b}>{EMPTY_PL[b]}</option>)}
          </select>
        </Field>
      </div>
      <label className="check" style={{ marginBottom: 10 }}>
        <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} /> Slot wymagany
      </label>
      <Field label="Podpowiedź treści (dla AI/redaktora)"
        hint="Opis OCZEKIWANEJ treści tego slotu — dla obrazu: temat/styl/nastrój do generowania AI, dla tekstu: o czym ma być. Może być pre-wypełnione z prompts.json przy imporcie.">
        <textarea value={contentHint} onChange={e => setContentHint(e.target.value)} rows={2} />
      </Field>
      <Alert kind="err">{err}</Alert>
      {dirty && <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={save}>{busy ? 'Zapisywanie…' : 'Zapisz slot'}</button>}
    </div>
  )
}

const ROLE_PL = { layout: 'rozdział (layout)', section: 'sekcja', header: 'nagłówek', footer: 'stopka' }

function PartialEditor({ partial, onPartialSaved, onSlotSaved }) {
  const [open, setOpen] = useState(false)
  const [selectionPrompt, setSelectionPrompt] = useState(partial.selection_prompt)
  const [capMin, setCapMin] = useState(partial.capacity_min_chars ?? '')
  const [capMax, setCapMax] = useState(partial.capacity_max_chars ?? '')
  const [alternatives, setAlternatives] = useState((partial.alternatives || []).join(', '))
  const [exclusionPrompt, setExclusionPrompt] = useState(partial.exclusion_prompt || '')
  const [pageBreakPolicy, setPageBreakPolicy] = useState(partial.page_break_policy || 'splittable')
  const [allowContinuation, setAllowContinuation] = useState(partial.allow_continuation !== false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [renderBusy, setRenderBusy] = useState(false)
  const [renderJobId, setRenderJobId] = useState(null)
  const [activeSlot, setActiveSlot] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewErr, setPreviewErr] = useState('')

  useEffect(() => {
    if (!open) return
    api.previewPartial(partial.id, activeSlot).then(setPreviewHtml).catch(e => setPreviewErr(e.message))
  }, [open, partial.id, activeSlot])

  const savePartial = async () => {
    setBusy(true); setErr('')
    try {
      const body = {
        selection_prompt: selectionPrompt,
        capacity_min_chars: capMin === '' ? null : Number(capMin),
        capacity_max_chars: capMax === '' ? null : Number(capMax),
        alternatives: alternatives.split(',').map(s => s.trim()).filter(Boolean),
      }
      if (partial.role === 'section') { body.exclusion_prompt = exclusionPrompt; body.page_break_policy = pageBreakPolicy }
      if (partial.role === 'layout') { body.allow_continuation = allowContinuation }
      const updated = await api.patchPartial(partial.id, body)
      onPartialSaved(updated)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const runRenderTest = async () => {
    setRenderBusy(true); setErr('')
    try {
      const values = {}
      partial.slots.forEach(s => { if (s.content_type !== 'image' && s.content_type !== 'generated_image') values[s.data_content] = `[Przykład: ${s.label}]` })
      const res = await api.renderTest(partial.id, { values })
      setRenderJobId(res.job_id)
    } catch (e) { setErr(e.message) } finally { setRenderBusy(false) }
  }

  const renderJob = usePolling(api.job, renderJobId, {
    onDone: (j) => downloadJobResult(j.id, `${partial.slug}-test.pdf`),
  })

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div>
          <h3 style={{ marginBottom: 4 }}>{open ? '▾' : '▸'} {partial.slug}</h3>
          <div className="mono small">{partial.file_path}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {partial.role && <span className="pill">{ROLE_PL[partial.role] || partial.role}</span>}
          {!partial.role && partial.is_page_template && <span className="pill">strona</span>}
          {!partial.role && partial.is_global && <span className="pill">globalny</span>}
          <span className="pill">{partial.slots.length} slotów</span>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 16 }}>
          <Alert kind="err">{err}</Alert>
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <Field label="Selection prompt" hint="Kiedy używać / kiedy nie używać / preferowana treść — opis dla Composition Engine.">
                <textarea value={selectionPrompt} onChange={e => setSelectionPrompt(e.target.value)} rows={3} />
              </Field>
              {partial.role === 'section' && (
                <>
                  <Field label="Exclusion prompt" hint="Warunek negatywny — kiedy NIE stosować tej sekcji (np. „brak nagłówka X”).">
                    <textarea value={exclusionPrompt} onChange={e => setExclusionPrompt(e.target.value)} rows={2} />
                  </Field>
                  <Field label="Dzielenie na kolejne strony">
                    <select value={pageBreakPolicy} onChange={e => setPageBreakPolicy(e.target.value)}>
                      <option value="splittable">może być dzielona (splittable)</option>
                      <option value="atomic">nie może być dzielona (atomic) — cała na kolejną stronę</option>
                    </select>
                  </Field>
                </>
              )}
              {partial.role === 'layout' && (
                <label className="check" style={{ marginBottom: 16 }}>
                  <input type="checkbox" checked={allowContinuation} onChange={e => setAllowContinuation(e.target.checked)} />
                  Ten rozdział może kontynuować się na kolejnej fizycznej stronie (normalny przypadek dla ebook/prezentacja)
                </label>
              )}
              <div className="row">
                <Field label="Pojemność min. (znaki)"><input type="number" value={capMin} onChange={e => setCapMin(e.target.value)} /></Field>
                <Field label="Pojemność maks. (znaki)"><input type="number" value={capMax} onChange={e => setCapMax(e.target.value)} /></Field>
              </div>
              <Field label="Alternatywy (przy overflow)" hint="Slugi partiali oddzielone przecinkiem.">
                <input value={alternatives} onChange={e => setAlternatives(e.target.value)} />
              </Field>
              <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={savePartial}>{busy ? 'Zapisywanie…' : 'Zapisz partial'}</button>
                {partial.is_page_template && (
                  <button type="button" className="btn btn-sm btn-ghost" disabled={renderBusy || (renderJob && renderJob.status === 'running')} onClick={runRenderTest}>
                    {renderJob && renderJob.status === 'running' ? 'Renderowanie…' : 'Renderuj test (PDF)'}
                  </button>
                )}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div className="lbl" style={{ marginBottom: 8 }}>Podgląd (przykładowa treść)</div>
              <Alert kind="err">{previewErr}</Alert>
              <div className="preview-frame-wrap" style={{ border: '1px solid var(--border-2)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <iframe title="podgląd partiala" srcDoc={previewHtml} sandbox="" style={{ width: '100%', height: 340, border: 'none', background: '#fff' }} />
              </div>
              <div className="muted small" style={{ marginTop: 6 }}>Kliknij pole slotu po lewej, aby podświetlić go w podglądzie (reszta przygasa).</div>
            </div>
          </div>
          <div className="lbl" style={{ marginBottom: 10 }}>Sloty</div>
          {partial.slots.map(s => <SlotEditor key={s.id} slot={s} onSaved={onSlotSaved} onActivate={setActiveSlot} />)}
        </div>
      )}
    </Card>
  )
}

function TemplateMetaCard({ tpl, onSaved }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(tpl.name)
  const [slug, setSlug] = useState(tpl.slug)
  const [documentType, setDocumentType] = useState(tpl.document_type)
  const [pageSize, setPageSize] = useState(tpl.page_size)
  const [orientation, setOrientation] = useState(tpl.orientation)
  const [imageStylePrompt, setImageStylePrompt] = useState(tpl.image_style_prompt || '')
  const [imageColorPrompt, setImageColorPrompt] = useState(tpl.image_color_prompt || '')
  const [generalAssumptions, setGeneralAssumptions] = useState(tpl.general_assumptions || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const sectioned = tpl.composition_mode === 'sectioned'

  const dirty = name !== tpl.name || slug !== tpl.slug || documentType !== tpl.document_type ||
    pageSize !== tpl.page_size || orientation !== tpl.orientation ||
    (sectioned && (imageStylePrompt !== (tpl.image_style_prompt || '') || imageColorPrompt !== (tpl.image_color_prompt || '') ||
                  generalAssumptions !== (tpl.general_assumptions || '')))

  const save = async () => {
    setBusy(true); setErr('')
    try {
      const body = { name, slug, document_type: documentType, page_size: pageSize, orientation }
      if (sectioned) { body.image_style_prompt = imageStylePrompt; body.image_color_prompt = imageColorPrompt; body.general_assumptions = generalAssumptions }
      const updated = await api.patchTemplate(tpl.id, body)
      onSaved(updated)
      setOpen(false)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <Card title={`${open ? '▾' : '▸'} Metadane szablonu`} right={<button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(o => !o)}>{open ? 'Zwiń' : 'Edytuj'}</button>}>
      {open ? (
        <>
          <Alert kind="err">{err}</Alert>
          <div className="row">
            <Field label="Nazwa"><input value={name} onChange={e => setName(e.target.value)} /></Field>
            <Field label="Slug"><input value={slug} onChange={e => setSlug(e.target.value.trim())} /></Field>
          </div>
          <div className="row">
            <Field label="Typ dokumentu">
              <select value={documentType} onChange={e => setDocumentType(e.target.value)}>
                <option value="ebook">ebook / dokument</option>
                <option value="raport">raport</option>
                <option value="prezentacja">prezentacja</option>
                <option value="ulotka">ulotka</option>
              </select>
            </Field>
            <Field label="Format strony">
              <select value={pageSize} onChange={e => setPageSize(e.target.value)}>
                <option value="A4">A4</option>
                <option value="Letter">Letter</option>
                <option value="16:9">16:9 (slajd)</option>
                <option value="4:3">4:3 (slajd)</option>
              </select>
            </Field>
            <Field label="Orientacja">
              <select value={orientation} onChange={e => setOrientation(e.target.value)}>
                <option value="portrait">pionowa</option>
                <option value="landscape">pozioma</option>
              </select>
            </Field>
          </div>
          {sectioned && (
            <>
              <div className="divider" />
              <div className="lbl" style={{ marginBottom: 10 }}>Prompty ogólne (tryb sekcyjny)</div>
              <Field label="Ogólne założenia"><textarea value={generalAssumptions} onChange={e => setGeneralAssumptions(e.target.value)} rows={3} /></Field>
              <Field label="Stylistyka zdjęć"><textarea value={imageStylePrompt} onChange={e => setImageStylePrompt(e.target.value)} rows={2} /></Field>
              <Field label="Kolorystyka"><textarea value={imageColorPrompt} onChange={e => setImageColorPrompt(e.target.value)} rows={2} /></Field>
            </>
          )}
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !dirty} onClick={save}>
            {busy ? 'Zapisywanie…' : 'Zapisz metadane'}
          </button>
        </>
      ) : (
        <div className="muted small">
          {tpl.slug} · {tpl.document_type} · {tpl.page_size}/{tpl.orientation} · tryb: {sectioned ? 'sekcyjny' : 'jednostronicowy'}
        </div>
      )}
    </Card>
  )
}

export default function TemplateDetail() {
  const { id } = useParams()
  const nav = useNavigate()
  const [tpl, setTpl] = useState(null)
  const [err, setErr] = useState('')
  const [pubBusy, setPubBusy] = useState(false)
  const [reviewJobId, setReviewJobId] = useState(null)
  const [reviewBusy, setReviewBusy] = useState(false)
  const [reviewResult, setReviewResult] = useState(null)
  const [fillJobId, setFillJobId] = useState(null)
  const [fillBusy, setFillBusy] = useState(false)
  const [fillResult, setFillResult] = useState(null)
  const [delBusy, setDelBusy] = useState(false)

  const load = () => api.template(id).then(setTpl).catch(e => setErr(e.message))
  useEffect(() => { load() }, [id])

  const remove = async () => {
    if (!tpl || !window.confirm(`Usunąć szablon „${tpl.name}” bezpowrotnie?`)) return
    setDelBusy(true); setErr('')
    try { await api.deleteTemplate(id); nav('/admin/templates') }
    catch (e) { setErr(e.message); setDelBusy(false) }
  }

  const patchPartialInState = (updated) => setTpl(t => ({ ...t, partials: t.partials.map(p => p.id === updated.id ? { ...p, ...updated } : p) }))
  const patchSlotInState = (updated) => setTpl(t => ({
    ...t, partials: t.partials.map(p => ({ ...p, slots: p.slots.map(s => s.id === updated.id ? updated : s) })),
  }))

  const publish = async () => {
    setPubBusy(true); setErr('')
    try { await api.publishTemplate(id); load() } catch (e) { setErr(e.message) } finally { setPubBusy(false) }
  }

  const runReview = async () => {
    setReviewBusy(true); setErr(''); setReviewResult(null)
    try { const res = await api.reviewTemplate(id); setReviewJobId(res.job_id) }
    catch (e) { setErr(e.message) } finally { setReviewBusy(false) }
  }

  const reviewJob = usePolling(api.job, reviewJobId, {
    onDone: async (j) => { try { setReviewResult(await api.jobResult(j.id)) } catch (e) { setErr(e.message) } },
    onError: (j) => setErr(j.error_msg || 'AI Reviewer zakończył się błędem'),
  })
  const reviewElapsed = useJobElapsed(reviewJob)

  const runAiFillPrompts = async () => {
    setFillBusy(true); setErr(''); setFillResult(null)
    try { const res = await api.aiFillPromptsTemplate(id); setFillJobId(res.job_id) }
    catch (e) { setErr(e.message) } finally { setFillBusy(false) }
  }

  const fillJob = usePolling(api.job, fillJobId, {
    onDone: async (j) => {
      try {
        const result = await api.jobResult(j.id)
        setFillResult(result)
        load()   // odśwież szablon/partiale/sloty — uzupełnione pola mają się pojawić bez ręcznego odświeżania strony
      } catch (e) { setErr(e.message) }
    },
    onError: (j) => setErr(j.error_msg || 'Uzupełnianie promptów przez AI zakończyło się błędem'),
  })
  const fillElapsed = useJobElapsed(fillJob)

  const fillResultSummary = (r) => {
    if (!r) return ''
    if (r.message) return r.message
    const parts = []
    if ((r.template_fields_filled || []).length) parts.push('założenia szablonu')
    const partialsCount = Object.keys(r.partials_filled || {}).length
    if (partialsCount) parts.push(`${partialsCount} ${partialsCount === 1 ? 'sekcja' : 'sekcje'} (selection_prompt/exclusion_prompt)`)
    const slotsCount = Object.values(r.slots_filled || {}).reduce((n, arr) => n + arr.length, 0)
    if (slotsCount) parts.push(`${slotsCount} ${slotsCount === 1 ? 'slot' : 'slotów'} (podpowiedź treści)`)
    return parts.length ? `Uzupełniono: ${parts.join(', ')}.` : 'Nie znaleziono nic do uzupełnienia.'
  }

  if (err && !tpl) return <div className="content"><Alert kind="err">{err}</Alert></div>
  if (!tpl) return <div className="content"><div className="empty">Wczytywanie…</div></div>

  const hasErrors = (tpl.validation_errors || []).length > 0

  return (
    <>
      <div className="topbar">
        <div>
          <button className="btn btn-sm btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav('/admin/templates')}>‹ Szablony</button>
          <h1>{tpl.name}</h1>
          <div className="sub">{tpl.slug} · {tpl.document_type} · {tpl.page_size}/{tpl.orientation} · wersja {tpl.current_version}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span className={`pill ${tpl.status === 'published' ? 'pill-done' : ''}`}>{tpl.status === 'published' ? 'opublikowany' : 'roboczy'}</span>
          <button className="btn" disabled={reviewBusy || (reviewJobId && !reviewResult && !err)} onClick={runReview}>
            {reviewBusy || (reviewJobId && !reviewResult) ? 'Ocena AI…' : '✦ Uruchom AI Reviewer'}
          </button>
          <button className="btn" disabled={fillBusy || (fillJobId && !fillResult && !err)} onClick={runAiFillPrompts}>
            {fillBusy || (fillJobId && !fillResult) ? 'Uzupełnianie…' : '✦ AI: uzupełnij prompty'}
          </button>
          <button className="btn btn-primary" disabled={pubBusy || hasErrors || tpl.status === 'published'} onClick={publish}>
            {tpl.status === 'published' ? 'Opublikowany' : (pubBusy ? 'Publikowanie…' : 'Publikuj')}
          </button>
          <button className="btn btn-danger" disabled={delBusy} onClick={remove}>{delBusy ? 'Usuwanie…' : 'Usuń szablon'}</button>
        </div>
      </div>
      <div className="content">
        <Alert kind="err">{err}</Alert>
        <TemplateMetaCard tpl={tpl} onSaved={setTpl} />
        {hasErrors && (
          <Alert kind="err">
            <b>Błędy walidacji (blokują publikację):</b>
            <ul style={{ margin: '6px 0 0 18px' }}>{tpl.validation_errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </Alert>
        )}
        {(tpl.validation_warnings || []).length > 0 && (
          <Alert kind="warn">
            <b>Ostrzeżenia:</b>
            <ul style={{ margin: '6px 0 0 18px' }}>{tpl.validation_warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </Alert>
        )}
        {reviewJob && (reviewJob.status === 'queued' || reviewJob.status === 'running') && (
          <>
            <Alert kind="info">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span><span className="spin-sm" style={{ marginRight: 8 }} />AI Reviewer w toku…</span>
                <span className="mono small">{reviewElapsed}</span>
              </div>
            </Alert>
            <JobProgress job={reviewJob} showTime={false} />
          </>
        )}
        {reviewResult && (
          <Card title="Werdykt AI Reviewera" right={<VerdictBadge verdict={reviewResult.verdict} />}>
            {(reviewResult.notes || []).length === 0 ? <div className="muted small">Brak uwag.</div> : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>{reviewResult.notes.map((n, i) => <li key={i} style={{ marginBottom: 6 }}>{n}</li>)}</ul>
            )}
          </Card>
        )}
        {fillJob && (fillJob.status === 'queued' || fillJob.status === 'running') && (
          <>
            <Alert kind="info">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span><span className="spin-sm" style={{ marginRight: 8 }} />AI uzupełnia prompty…</span>
                <span className="mono small">{fillElapsed}</span>
              </div>
            </Alert>
            <JobProgress job={fillJob} showTime={false} />
          </>
        )}
        {fillResult && <Alert kind="ok">{fillResultSummary(fillResult)}</Alert>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 4 }}>
          {tpl.partials.map(p => (
            <PartialEditor key={p.id} partial={p} onPartialSaved={patchPartialInState} onSlotSaved={patchSlotInState} />
          ))}
        </div>
      </div>
    </>
  )
}
